import { motion } from "framer-motion"
import Head from "next/head"
import { Footer } from "../components/Footer"
import { Header } from "../components/Header"
import { PageProps } from "../utils/types"
import { sendSupportMail } from "../utils/useMail"

const headerVariants = {
  animate: {
    opacity: 1,
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants1 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.05,
    },
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants2 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.1,
    },
  },
  initial: {
    opacity: 0,
  },
}

export default function PrivacyPage(props: PageProps) {
  return (
    <>
      <Head>
        <title>Frontender | Speed up your front-end workflow</title>
        <meta name="theme-color" content="#18181b" />
        <meta property="og:image" content="https://www.frontender.io/og.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="627" />
        <meta property="og:title" content="Frontender" />
        <meta
          property="description"
          content="Speed up your front-end workflow."
        />
        <meta
          property="og:description"
          content="Speed up your front-end workflow."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.frontender.io/" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="msapplication-TileColor" content="#da532c" />
      </Head>
      <motion.div
        initial="initial"
        animate="animate"
        className="flex min-h-screen select-none bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-800 p-4"
      >
        <div className="flex flex-1 flex-col overflow-hidden border-zinc-700 p-4 pt-1 sm:border sm:p-7 sm:pt-3">
          <Header />
          <div className="max-w-screen-2xl py-10 sm:px-4 lg:px-12 lg:py-16 xl:py-20">
            <motion.div
              variants={headerVariants}
              className="mb-6 inline-block bg-gradient-to-r from-sky-300 to-fuchsia-300 bg-clip-text pb-1 text-3xl font-semibold leading-tight text-transparent sm:text-5xl sm:tracking-tight"
            >
              Privacy Policy
            </motion.div>
            <motion.div
              variants={rowVariants1}
              className="mb-6 max-w-prose space-y-4 leading-relaxed text-zinc-200"
            >
              Last updated: 20 February 2023
            </motion.div>
            <motion.div
              variants={rowVariants2}
              className="max-w-prose space-y-4 leading-relaxed text-zinc-400"
            >
              <p>
                Frontender is provided by Hubition, a company located at Lange
                Viestraat 2b, 3511 BK, Utrecht, The Netherlands, registration
                number 53931459.
              </p>
              <p>
                We process your data to the minimum amount required to make
                Frontender function. This is how it works:
              </p>
              <ul className="list-disc space-y-2 pl-4">
                <li>
                  Frontender uses Google Cloud to store your account details and
                  settings. Their servers are located within the European Union.
                  We only store basic account details and the settings you can
                  set in Frontender, nothing more.
                </li>
                <li>
                  Our order process is conducted by our online reseller
                  Paddle.com. Paddle.com is the Merchant of Record for all our
                  orders. Paddle provides all customer service inquiries and
                  handles returns.
                </li>
                <li>
                  We use Postmark.com to send you emails. Postmark is a service
                  provided by Wildbit, Inc. Their servers are located within the
                  European Union.
                </li>
                <li>
                  We use PostHog.com to collect anonymous usage data. This data
                  is used to improve Frontender.
                </li>
                <li>
                  We use EU-based servers from Vercel.com to host our website
                  and API.
                </li>
              </ul>
              <p>
                To request more details or delete your data from our systems,
                please{" "}
                <button
                  type="button"
                  onClick={sendSupportMail}
                  className="text-zinc-200 underline decoration-zinc-500 decoration-2 underline-offset-4"
                >
                  click here
                </button>{" "}
                to send us an email. We will reply within 5 business days (but
                usually much faster).
              </p>
            </motion.div>
          </div>
          <Footer
            acceptedCookies={props.acceptedCookies}
            setAcceptedCookies={props.setAcceptedCookies}
          />
        </div>
        <div className="animate-blob pointer-events-none fixed -right-[60vw] top-[20vh] h-[120vh] w-[120vw] rounded-full bg-fuchsia-800 opacity-10 blur-3xl" />
      </motion.div>
    </>
  )
}

PrivacyPage.displayName = "PrivacyPage"
